// ============================================================
// usePlatformWebhooks — Platform Control webhook configuration.
//
// Backend-ready: queries sq_webhooks when the table exists. No
// fake seeded webhooks; surfaces show an honest empty state and
// distinguish "no webhooks configured" from "table not deployed".
// ============================================================

import { useEffect, useState } from 'react'
import { getRuntime } from './_mode'
import { captureHookFailure, withSpan } from '../observability/sentry'

export interface PlatformWebhook {
  id: string
  label: string
  url: string
  event_filter?: string
  status: 'active' | 'paused' | 'failing'
  last_delivered_at?: string
  failure_count?: number
}

export interface UsePlatformWebhooksResult {
  webhooks: PlatformWebhook[]
  loading: boolean
  error: string | null
  awaitingBackend: boolean
}

export function usePlatformWebhooks(): UsePlatformWebhooksResult {
  const [webhooks, setWebhooks] = useState<PlatformWebhook[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [awaitingBackend, setAwaitingBackend] = useState(false)

  useEffect(() => {
    let cancelled = false
    const { live, supabase } = getRuntime()

    async function load() {
      if (!live || !supabase) {
        if (cancelled) return
        setAwaitingBackend(true)
        setWebhooks([])
        setLoading(false)
        return
      }

      try {
        const { data, error: err } = await withSpan(
          'sq_webhooks.select',
          'db.query',
          async () =>
            supabase
              .from('sq_webhooks')
              .select('id,label,url,event_filter,status,last_delivered_at,failure_count')
              .order('last_delivered_at', { ascending: false }),
        )
        if (cancelled) return
        if (err) {
          const message = err.message || ''
          const missing =
            /relation .* does not exist/i.test(message) ||
            err.code === '42P01'
          setError(message)
          setWebhooks([])
          setAwaitingBackend(missing)
          if (!missing) {
            captureHookFailure('usePlatformWebhooks', 'live_error', err, {
              table: 'sq_webhooks',
            })
          }
        } else {
          setWebhooks((data as PlatformWebhook[]) || [])
          setAwaitingBackend(false)
        }
      } catch (err) {
        if (cancelled) return
        setError((err as Error).message)
        setWebhooks([])
      }
      if (!cancelled) setLoading(false)
    }

    load()
    return () => {
      cancelled = true
    }
  }, [])

  return { webhooks, loading, error, awaitingBackend }
}
